import { useCallback, useEffect, useRef, useState } from 'react';

import {
  createCoreConfigurationApi,
  type CoreConfigurationApi,
  type CoreConfigurationStatus,
} from './coreConfigurationApi';

export const CORE_CONFIGURATION_LOAD_ERROR = 'Could not load Core configuration. Try again.';
export const CORE_CONFIGURATION_UPDATE_ERROR = 'Could not update Core configuration. Try again.';
export const CORE_CONFIGURATION_REACTIVATE_ERROR = 'Could not re-activate Field Ops webhooks. Try again.';

export const CORE_CONFIGURATION_TOKEN_SUCCESS = 'API access token is valid.';
export const CORE_CONFIGURATION_PROVISION_WEBHOOKS_SUCCESS = 'Missing Field Ops webhooks were created.';
export const CORE_CONFIGURATION_REACTIVATE_SUCCESS = 'Failed Field Ops webhooks were re-activated.';

export type CoreConfigurationMutation = 'provisionToken' | 'provisionWebhooks' | 'reactivateWebhooks';

export type UseCoreConfigurationOptions = {
  api?: CoreConfigurationApi;
};

export type UseCoreConfigurationResult = {
  status: CoreConfigurationStatus | null;
  loading: boolean;
  errorMessage: string | null;
  announcement: string;
  mutationInFlight: CoreConfigurationMutation | null;
  reload: () => void;
  provisionToken: () => Promise<void>;
  provisionWebhooks: () => Promise<void>;
  reactivateWebhooks: () => Promise<void>;
};

const isAbort = (error: unknown, signal?: AbortSignal): boolean =>
  signal?.aborted === true || (error instanceof DOMException && error.name === 'AbortError');

export const useCoreConfiguration = ({ api }: UseCoreConfigurationOptions = {}): UseCoreConfigurationResult => {
  const defaultApiRef = useRef<CoreConfigurationApi | null>(null);
  if (api === undefined && defaultApiRef.current === null) defaultApiRef.current = createCoreConfigurationApi();
  const resolvedApi = api ?? defaultApiRef.current;
  if (resolvedApi === null) throw new Error('Core Configuration API was not initialized.');

  const [status, setStatus] = useState<CoreConfigurationStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [announcement, setAnnouncement] = useState('');
  const [mutationInFlight, setMutationInFlight] = useState<CoreConfigurationMutation | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  const mountedRef = useRef(true);
  const mutationInFlightRef = useRef<CoreConfigurationMutation | null>(null);
  const loadControllerRef = useRef<AbortController | null>(null);
  const loadSequenceRef = useRef(0);

  const setMutation = useCallback((operation: CoreConfigurationMutation | null): void => {
    mutationInFlightRef.current = operation;
    setMutationInFlight(operation);
  }, []);

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
      loadSequenceRef.current += 1;
      loadControllerRef.current?.abort();
      loadControllerRef.current = null;
    };
  }, []);

  useEffect(() => {
    loadSequenceRef.current += 1;
    loadControllerRef.current?.abort();

    const controller = new AbortController();
    const requestSequence = loadSequenceRef.current;
    loadControllerRef.current = controller;
    setLoading(true);
    setErrorMessage(null);
    setAnnouncement('');

    void (async () => {
      try {
        const nextStatus = await resolvedApi.getStatus({ signal: controller.signal });
        if (!mountedRef.current || controller.signal.aborted || requestSequence !== loadSequenceRef.current) return;
        setStatus(nextStatus);
      } catch (error) {
        if (isAbort(error, controller.signal) || !mountedRef.current || requestSequence !== loadSequenceRef.current) {
          return;
        }
        setErrorMessage(CORE_CONFIGURATION_LOAD_ERROR);
      } finally {
        if (mountedRef.current && requestSequence === loadSequenceRef.current) {
          setLoading(false);
          if (loadControllerRef.current === controller) loadControllerRef.current = null;
        }
      }
    })();

    return () => {
      controller.abort();
    };
  }, [reloadToken, resolvedApi]);

  const reload = useCallback((): void => {
    if (mutationInFlightRef.current !== null) return;
    setReloadToken(token => token + 1);
  }, []);

  const runMutation = useCallback(
    async (
      operation: CoreConfigurationMutation,
      mutate: () => Promise<CoreConfigurationStatus>,
      successAnnouncement: string,
      failureMessage: string,
    ): Promise<void> => {
      if (mutationInFlightRef.current !== null || loading) return;

      setMutation(operation);
      setErrorMessage(null);
      setAnnouncement('');
      try {
        const nextStatus = await mutate();
        if (!mountedRef.current) return;
        setStatus(nextStatus);
        setAnnouncement(successAnnouncement);
      } catch {
        if (mountedRef.current) setErrorMessage(failureMessage);
      } finally {
        if (mountedRef.current) setMutation(null);
      }
    },
    [loading, setMutation],
  );

  const provisionToken = useCallback(
    () =>
      runMutation(
        'provisionToken',
        () => resolvedApi.provisionApiToken(),
        CORE_CONFIGURATION_TOKEN_SUCCESS,
        CORE_CONFIGURATION_UPDATE_ERROR,
      ),
    [resolvedApi, runMutation],
  );

  const provisionWebhooks = useCallback(
    () =>
      runMutation(
        'provisionWebhooks',
        () => resolvedApi.provisionWebhooks(),
        CORE_CONFIGURATION_PROVISION_WEBHOOKS_SUCCESS,
        CORE_CONFIGURATION_UPDATE_ERROR,
      ),
    [resolvedApi, runMutation],
  );

  const reactivateWebhooks = useCallback(
    () =>
      runMutation(
        'reactivateWebhooks',
        () => resolvedApi.reactivateWebhooks(),
        CORE_CONFIGURATION_REACTIVATE_SUCCESS,
        CORE_CONFIGURATION_REACTIVATE_ERROR,
      ),
    [resolvedApi, runMutation],
  );

  return {
    status,
    loading,
    errorMessage,
    announcement,
    mutationInFlight,
    reload,
    provisionToken,
    provisionWebhooks,
    reactivateWebhooks,
  };
};
